import type { AssetId } from '@shapeshiftoss/caip'
import type { Asset, AssetsByIdPartial } from '@shapeshiftoss/types'

import { getMixPanel } from './mixPanelSingleton'
import type { TrackOpportunityProps } from './types'
import { MixPanelEvent } from './types'

import { getChainAdapterManager } from '@/context/PluginProvider/chainAdapterSingleton'
import type { AppleSearchAdsAttributionData } from '@/lib/appleSearchAds/attributionData'
import { bnOrZero } from '@/lib/bignumber/bignumber'

// Returns an altered path when necessary or null if the path should not be tracked for privacy
export const mapMixpanelPathname = (pathname: string, assets: AssetsByIdPartial): string | null => {
  switch (true) {
    case pathname.startsWith('/accounts/'): {
      return null
    }
    case pathname.startsWith('/assets/'): {
      // example path
      // /assets/eip155:1/slip44:60
      const parts = pathname.split('/')
      const [_, assetLiteral, chainId, assetIdParts, ...additionalAssetIdParts] = parts
      const maybeAssetId = [chainId, assetIdParts, ...additionalAssetIdParts].join('/')
      const mixpanelAssetId = maybeAssetId && getMaybeCompositeAssetSymbol(maybeAssetId, assets)
      const newParts = [_, assetLiteral]
      if (mixpanelAssetId) newParts.push(mixpanelAssetId)
      return newParts.join('/')
    }
    default:
      return pathname
  }
}

export const assetToCompositeSymbol = (asset: Asset): string => {
  const { chainId } = asset
  const networkName = getChainAdapterManager().get(chainId)?.getDisplayName()
  return `${networkName}.${asset?.symbol}`
}

export const getMaybeCompositeAssetSymbol = (
  assetId: AssetId,
  assetsById: AssetsByIdPartial,
): string => {
  const asset = assetsById[assetId]
  if (!asset) return assetId // better than 'unknown asset'
  return assetToCompositeSymbol(asset)
}

export const trackOpportunityEvent = (
  event: MixPanelEvent,
  properties: TrackOpportunityProps,
  assetsById: AssetsByIdPartial,
) => {
  const mixpanel = getMixPanel()
  const { opportunity, cryptoAmounts, fiatAmounts, element } = properties
  const eventData = {
    provider: opportunity.provider,
    type: opportunity.type,
    version: opportunity.version,
    name: opportunity.name,
    asset: getMaybeCompositeAssetSymbol(opportunity.assetId, assetsById),
    underlyingAssets: opportunity.underlyingAssetIds.map(assetId =>
      getMaybeCompositeAssetSymbol(assetId, assetsById),
    ),
    ...(fiatAmounts && {
      fiatAmounts: fiatAmounts.map(fiatAmount => bnOrZero(fiatAmount).toNumber()),
    }),
    ...(cryptoAmounts &&
      Object.fromEntries(
        cryptoAmounts.map(claimAmount => [
          getMaybeCompositeAssetSymbol(claimAmount.assetId, assetsById),
          claimAmount.amountCryptoHuman,
        ]),
      )),
    ...(element && { element }),
  }
  mixpanel?.track(event, eventData)
}

export const trackAppleSearchAdsAttribution = (data: AppleSearchAdsAttributionData) => {
  const mixpanel = getMixPanel()
  if (!mixpanel) return

  // Extract keyword ID (use 'unknown' as fallback per requirements)
  const keywordId = data.keywordId?.toString() || 'unknown'

  const keyword = 'unknown_keyword' // Apple API doesn't return keyword text

  // Track the event with all available campaign data
  mixpanel.track(MixPanelEvent.AdAttributionReceived, {
    ua_source: 'apple_search_ads',
    apple_keyword: keyword,
    apple_keyword_id: keywordId,
    campaign_id: data.campaignId,
    ad_group_id: data.adGroupId,
    ad_id: data.adId,
    org_id: data.orgId,
    country: data.countryOrRegion,
    conversion_type: data.conversionType,
    claim_type: data.claimType,
    ...(data.clickDate && { click_date: data.clickDate }),
  })

  // Set first-touch properties using set_once to ensure they persist after aliasing
  mixpanel.people.set_once({
    ft_source: 'apple_search_ads',
    ft_apple_keyword: keyword,
    ft_apple_keyword_id: keywordId,
    ft_campaign_id: data.campaignId,
    ft_ad_group_id: data.adGroupId,
    ft_ad_id: data.adId,
    ft_country: data.countryOrRegion,
    ft_conversion_type: data.conversionType,
  })
}
